import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Archive, Pencil, Palette, Home, X, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import clsx from "clsx";
import type { Board } from "../../types";
import { useStore } from "../../stores/store";
import { getBoardColor, getBoardInitials } from "../../lib/boardVisuals";
import { BOARD_ICON_MAP } from "./boardIcons";
import { BoardEditorModal } from "./BoardEditorModal";
import { ArchivedBoardsModal } from "./ArchivedBoardsModal";

const LONG_PRESS_MS = 500;

export function BoardTabs() {
  const boards = useStore((s) => s.boards);
  const activeBoardId = useStore((s) => s.activeBoardId);
  const switchBoard = useStore((s) => s.switchBoard);
  const archiveBoard = useStore((s) => s.archiveBoard);

  const [sheetBoard, setSheetBoard] = useState<Board | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<
    | { board: Board; x: number; y: number }
    | null
  >(null);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [editor, setEditor] = useState<
    | { mode: "create" }
    | { mode: "rename"; board: Board }
    | { mode: "visuals"; board: Board }
    | null
  >(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const longPressTriggered = useRef(false);
  const timerRef = useRef<number | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  function updateScrollAffordances() {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }

  useEffect(() => {
    updateScrollAffordances();
    const el = scrollerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollAffordances);
    ro.observe(el);
    return () => ro.disconnect();
  }, [boards.length]);

  const maskGradient = (() => {
    if (!canScrollLeft && !canScrollRight) return undefined;
    const stops: string[] = [];
    stops.push(canScrollLeft ? "transparent 0" : "black 0");
    if (canScrollLeft) stops.push("black 40px");
    if (canScrollRight) stops.push("black calc(100% - 40px)");
    stops.push(canScrollRight ? "transparent 100%" : "black 100%");
    return `linear-gradient(to right, ${stops.join(", ")})`;
  })();
  const maskStyle = maskGradient
    ? ({
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      } as React.CSSProperties)
    : undefined;

  const sorted = useMemo(() => {
    const visible = boards.filter((b) => !b.archived);
    return [...visible].sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return a.position - b.position;
    });
  }, [boards]);

  function clearLongPress() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function startLongPress(b: Board) {
    if (b.isPrimary) return;
    longPressTriggered.current = false;
    clearLongPress();
    timerRef.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      setSheetBoard(b);
    }, LONG_PRESS_MS);
  }

  function cancelLongPress() {
    clearLongPress();
  }

  async function handleSelect(b: Board) {
    clearLongPress();
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }
    if (b.id === activeBoardId) return;
    try {
      await switchBoard(b.id);
    } catch {
      // store handled
    }
  }

  useEffect(() => () => clearLongPress(), []);

  useEffect(() => {
    if (!sheetBoard && !menuAnchor) setConfirmingArchive(false);
  }, [sheetBoard, menuAnchor]);

  useEffect(() => {
    if (!menuAnchor) return;
    function close() {
      setMenuAnchor(null);
      setConfirmingArchive(false);
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menuAnchor]);

  async function handleArchive(id: number) {
    try {
      await archiveBoard(id);
    } catch {
      // ignore
    } finally {
      setSheetBoard(null);
      setMenuAnchor(null);
      setConfirmingArchive(false);
    }
  }

  return (
    <>
      <div className="relative w-fit max-w-[calc(100%-104px)] self-start">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-7 rounded-tr-xl border-r border-t border-white/5 bg-slate-950/70"
      />
      <nav
        ref={navRef}
        className="relative z-10 flex w-full items-end gap-0.5 px-1 pt-3"
      >
        <span className="mr-0.5 flex h-7 shrink-0 items-center self-end px-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Boards
        </span>
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollerRef.current?.scrollBy({ left: -160, behavior: "smooth" })}
            className="flex h-7 w-6 shrink-0 items-center justify-center self-end text-slate-400 transition hover:text-slate-100"
            aria-label="Rolar para boards anteriores"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <div
          ref={scrollerRef}
          onScroll={updateScrollAffordances}
          style={maskStyle}
          className="flex min-w-0 flex-1 items-end gap-0.5 overflow-x-auto"
        >
        {sorted.map((b) => {
          const active = activeBoardId === b.id;
          const palette = getBoardColor(b.title, b.color);
          const Icon = b.icon ? BOARD_ICON_MAP[b.icon as keyof typeof BOARD_ICON_MAP] : null;
          return (
            <div key={b.id} className="relative shrink-0">
              <button
                type="button"
                onClick={() => handleSelect(b)}
                onMouseDown={() => startLongPress(b)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(b)}
                onTouchEnd={cancelLongPress}
                onTouchCancel={cancelLongPress}
                onContextMenu={(e) => {
                  if (b.isPrimary) return;
                  e.preventDefault();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setMenuAnchor({
                    board: b,
                    x: rect.left,
                    y: rect.top,
                  });
                }}
                className={clsx(
                  "group/tab flex items-center gap-2 rounded-t-lg border border-b-0 py-2 text-[13px] font-medium transition",
                  active && !b.isPrimary ? "pl-3 pr-8" : "px-3",
                  b.isPrimary
                    ? active
                      ? "border-violet-400/40 bg-bg-0 text-violet-100 shadow-[0_-2px_10px_-4px_rgba(139,92,246,0.4)]"
                      : "border-violet-400/25 bg-violet-500/10 text-violet-200 hover:bg-violet-500/15 hover:text-violet-100"
                    : active
                      ? "border-white/10 bg-bg-0 text-slate-100 shadow-[0_-2px_10px_-4px_rgba(0,0,0,0.6)]"
                      : "border-white/5 bg-slate-800/40 text-slate-400 hover:bg-slate-800/70 hover:text-slate-200",
                )}
                title={b.title}
              >
                {Icon ? (
                  <Icon className={clsx("h-3.5 w-3.5", b.isPrimary ? "text-violet-300" : active ? "text-slate-100" : "text-slate-400")} />
                ) : b.isPrimary ? (
                  <Home className="h-3.5 w-3.5 text-violet-300" />
                ) : (
                  <span
                    className={clsx(
                      "inline-block h-2 w-2 rounded-full",
                      palette.bg,
                    )}
                  />
                )}
                <span className="max-w-[160px] truncate">{b.title}</span>
                {active && (
                  <span
                    className={clsx(
                      "pointer-events-none absolute inset-x-1 top-0 h-[2px] rounded-full",
                      palette.bg,
                    )}
                  />
                )}
              </button>
              {active && !b.isPrimary && (
                <button
                  type="button"
                  aria-label={`Opções de ${b.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const tabEl = e.currentTarget.previousElementSibling as HTMLElement | null;
                    if (!tabEl) return;
                    const rect = tabEl.getBoundingClientRect();
                    setMenuAnchor({ board: b, x: rect.left, y: rect.top });
                  }}
                  className="absolute right-1 top-0.5 z-10 flex h-4 w-5 items-center justify-center rounded text-slate-400 transition hover:bg-white/10 hover:text-slate-100"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              )}

            </div>
          );
        })}
        </div>
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollerRef.current?.scrollBy({ left: 160, behavior: "smooth" })}
            className="flex h-7 w-6 shrink-0 items-center justify-center self-end text-slate-400 transition hover:text-slate-100"
            aria-label="Rolar para próximos boards"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          onClick={() => setEditor({ mode: "create" })}
          className="ml-1 flex h-7 w-7 shrink-0 items-center justify-center self-end rounded-md text-slate-400 transition hover:bg-white/5 hover:text-violet-200"
          title="Novo quadro"
          aria-label="Novo quadro"
        >
          <Plus className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => setArchivedOpen(true)}
          className="ml-1 flex h-7 shrink-0 items-center gap-1 self-end rounded-md px-2 text-[11px] text-slate-500 transition hover:bg-white/5 hover:text-slate-300"
          title="Quadros arquivados"
        >
          <Archive className="h-3 w-3" />
          <span className="hidden sm:inline">Arquivados</span>
        </button>
      </nav>
      </div>

      <AnimatePresence>
        {sheetBoard && (
          <motion.div
            key="sheet-overlay"
            className="fixed inset-0 z-[60] flex items-end justify-center lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <div
              className="absolute inset-0 bg-black/65 backdrop-blur-sm"
              onClick={() => setSheetBoard(null)}
            />
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="glass relative w-full max-w-md rounded-t-2xl p-4 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />
              <div className="mb-3 flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-100">{sheetBoard.title}</p>
                <button
                  type="button"
                  onClick={() => setSheetBoard(null)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {!confirmingArchive ? (
                <div className="flex flex-col gap-1">
                  <SheetAction
                    icon={<Pencil className="h-4 w-4" />}
                    onClick={() => {
                      setEditor({ mode: "rename", board: sheetBoard });
                      setSheetBoard(null);
                    }}
                  >
                    Renomear
                  </SheetAction>
                  <SheetAction
                    icon={<Palette className="h-4 w-4" />}
                    onClick={() => {
                      setEditor({ mode: "visuals", board: sheetBoard });
                      setSheetBoard(null);
                    }}
                  >
                    Trocar ícone, cor
                  </SheetAction>
                  <SheetAction
                    icon={<Archive className="h-4 w-4" />}
                    onClick={() => setConfirmingArchive(true)}
                    danger
                  >
                    Arquivar
                  </SheetAction>
                </div>
              ) : (
                <div className="px-2 py-3">
                  <p className="mb-3 text-sm text-slate-200">
                    Arquivar <span className="font-semibold">{sheetBoard.title}</span>?
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingArchive(false)}
                      className="rounded-lg px-3 py-2 text-sm text-slate-300 transition hover:bg-white/5 hover:text-slate-100"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleArchive(sheetBoard.id)}
                      className="rounded-lg bg-rose-500/90 px-3 py-2 text-sm font-medium text-white transition hover:bg-rose-500"
                    >
                      Arquivar
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {menuAnchor && (
        <div
          className="fixed z-50 w-48 overflow-hidden rounded-lg border border-white/10 bg-slate-900/95 shadow-elevated backdrop-blur"
          style={{
            left: menuAnchor.x,
            top: menuAnchor.y - 8,
            transform: "translateY(-100%)",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {!confirmingArchive ? (
            <>
              <MenuItem
                icon={<Pencil className="h-3.5 w-3.5" />}
                onClick={() => {
                  const b = menuAnchor.board;
                  setMenuAnchor(null);
                  setEditor({ mode: "rename", board: b });
                }}
              >
                Renomear
              </MenuItem>
              <MenuItem
                icon={<Palette className="h-3.5 w-3.5" />}
                onClick={() => {
                  const b = menuAnchor.board;
                  setMenuAnchor(null);
                  setEditor({ mode: "visuals", board: b });
                }}
              >
                Trocar ícone, cor
              </MenuItem>
              <MenuItem
                icon={<Archive className="h-3.5 w-3.5" />}
                onClick={() => setConfirmingArchive(true)}
                danger
              >
                Arquivar
              </MenuItem>
            </>
          ) : (
            <div className="p-3">
              <p className="mb-2.5 text-xs text-slate-200">
                Arquivar <span className="font-semibold">{menuAnchor.board.title}</span>?
              </p>
              <div className="flex items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => setConfirmingArchive(false)}
                  className="rounded-md px-2.5 py-1 text-xs text-slate-300 transition hover:bg-white/5 hover:text-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleArchive(menuAnchor.board.id)}
                  className="rounded-md bg-rose-500/90 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-rose-500"
                >
                  Arquivar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <BoardEditorModal
        open={editor !== null}
        mode={editor?.mode ?? "create"}
        board={editor && "board" in editor ? editor.board : null}
        onClose={() => setEditor(null)}
      />
      <ArchivedBoardsModal open={archivedOpen} onClose={() => setArchivedOpen(false)} />
    </>
  );
}

function MenuItem({
  icon,
  onClick,
  danger,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition",
        danger
          ? "text-rose-300 hover:bg-rose-500/10"
          : "text-slate-200 hover:bg-white/5",
      )}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function SheetAction({
  icon,
  onClick,
  danger,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition",
        danger
          ? "text-rose-300 hover:bg-rose-500/10"
          : "text-slate-200 hover:bg-white/5",
      )}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
